/**
 * V1 source: customer/midas/components/ChatListRefChip.tsx
 *   (canonical V1 path: shared/chatBot/components/ChatListRefChip.tsx)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 1.5
 *
 * Style A — Inline mono link (Paper artboard `4QI-0`).
 *
 * Renders a `<list-ref>` citation as a quiet inline mono terracotta link
 * (no chip silhouette). Three states:
 *   - default: plain mono terracotta text, no border, no underline
 *   - hover:   underline + 11x11 arrow-up-right (slide-in 120ms)
 *   - active:  bold + persistent underline, no arrow
 *
 * Click dispatches openArtifactAtPage via parent `onClick` (G17 invariant —
 * same handler shape as V1 ChatListRefChip, drop-in prop replacement).
 *
 * One-time discoverability tooltip on first hover per browser, gated by
 * localStorage key `midas_v2_listref_tooltip_seen` (see §4.4 of the spec).
 */
import React, { useCallback, useId, useRef, useState } from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";

const STORAGE_KEY = "midas_v2_listref_tooltip_seen";
const TOOLTIP_AUTO_DISMISS_MS = 4000;
const TERRACOTTA = "#C24A1F";

// Module-level guard so a single tab only ever fires the tooltip once,
// even if multiple `<ChatListRefV2>` instances mount on the same page and
// the localStorage write hasn't propagated to all instances' state yet.
let tooltipFiredThisSession = false;

const readSeenFlag = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // localStorage may throw in private mode / sandboxed iframes.
    // Default to "seen" so we don't spam the tooltip if storage is broken.
    return true;
  }
};

const writeSeenFlag = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // No-op — see readSeenFlag note.
  }
};

export interface ChatListRefV2Props {
  /** Supplementary display/debug ID (e.g. "070"). NOT a join key. */
  listId: string;
  /** Canonical join key — `page` from the tag. Used by openArtifactAtPage. */
  page: number;
  /** Optional name from the tag's `name` attribute (e.g. "Emergency Stop Schematic"). */
  name?: string;
  /** Visible label (text between the tags, e.g. "List 070"). */
  label: string;
  /** True when the referenced page's tab is currently open in the dock. */
  isActive?: boolean;
  /** ACCEPTED but IGNORED in V2 — Style A drops "viewed" state (see spec §3). */
  isViewed?: boolean;
  /** Click handler — parent dispatches openArtifactAtPage. */
  onClick?: (payload: { listId: string; page: number; name?: string }) => void;
}

const ChatListRefV2: React.FC<ChatListRefV2Props> = ({
  listId,
  page,
  name,
  label,
  isActive = false,
  // isViewed deliberately destructured-but-unused to keep prop parity with V1.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isViewed = false,
  onClick,
}) => {
  const clickable = !!onClick;
  const { translate } = useTranslation("chatBot");
  const tooltipId = useId();

  // Read localStorage exactly once per mount.
  const [seen, setSeen] = useState<boolean>(() => readSeenFlag());
  const [hovered, setHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const dismissTimerRef = useRef<number | null>(null);

  const dismissTooltipPermanently = useCallback(() => {
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setShowTooltip(false);
    setSeen(true);
    tooltipFiredThisSession = true;
    writeSeenFlag();
  }, []);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    if (!seen && !tooltipFiredThisSession) {
      tooltipFiredThisSession = true; // claim immediately so siblings don't fire
      setShowTooltip(true);
      // Auto-dismiss after 4s — mark as seen.
      dismissTimerRef.current = window.setTimeout(() => {
        dismissTooltipPermanently();
      }, TOOLTIP_AUTO_DISMISS_MS);
    }
  }, [seen, dismissTooltipPermanently]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  const handleClick = useCallback(() => {
    if (!clickable) return;
    // Click also satisfies "user has discovered the link" — mark as seen.
    if (!seen) {
      dismissTooltipPermanently();
    }
    onClick?.({ listId, page, name });
  }, [clickable, seen, dismissTooltipPermanently, onClick, listId, page, name]);

  // Cleanup pending timer on unmount.
  React.useEffect(() => {
    return () => {
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, []);

  const ariaLabel = name
    ? `${label} (${name}) — page ${page}`
    : `${label} — page ${page}`;

  // Disabled (no onClick): plain mono span, no cursor, no interaction.
  if (!clickable) {
    return (
      <span
        className="font-mono text-[14px] font-normal leading-[24px]"
        style={{ color: TERRACOTTA, verticalAlign: "baseline" }}
        aria-label={ariaLabel}
        data-list-id={listId}
        data-page={page}
      >
        {label}
      </span>
    );
  }

  const showUnderline = isActive || hovered;
  const showArrow = hovered && !isActive;

  return (
    <span
      className="relative"
      style={{ display: "inline", verticalAlign: "baseline" }}
    >
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        aria-label={ariaLabel}
        aria-describedby={showTooltip ? tooltipId : undefined}
        data-list-id={listId}
        data-page={page}
        // Reset all UA button styles. Inline so it sits inside flowing prose.
        className="cursor-pointer bg-transparent p-0 font-mono outline-0 focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{
          display: "inline",
          verticalAlign: "baseline",
          border: 0,
          fontFamily: "'JetBrains Mono', system-ui, sans-serif",
          fontSize: "14px",
          fontWeight: isActive ? 600 : 400,
          lineHeight: "24px",
          color: TERRACOTTA,
          margin: "0 2px",
          borderBottom: showUnderline ? `1px solid ${TERRACOTTA}` : "1px solid transparent",
          paddingBottom: "1px",
          transition: "border-color 120ms ease-out",
          // focus-visible outline color (overridden via inline style — Tailwind
          // `outline-*` arbitrary value works but inline keeps it co-located).
          outlineColor: "rgba(194, 74, 31, 0.35)",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: "4px",
          }}
        >
          {label}
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke={TERRACOTTA}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            style={{
              flexShrink: 0,
              opacity: showArrow ? 1 : 0,
              transform: showArrow ? "translateX(0)" : "translateX(-2px)",
              transition: "opacity 120ms ease-out, transform 120ms ease-out",
              pointerEvents: "none",
            }}
          >
            <path d="M7 17L17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </span>
      </button>

      {showTooltip && (
        <span
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none absolute left-0 top-full z-10 mt-1 whitespace-nowrap rounded-md bg-midas-graphite px-2 py-1 font-mono text-[11px] text-midas-bone shadow-lg"
          style={{
            opacity: 1,
            transition: "opacity 160ms ease-out",
          }}
        >
          {translate("listRef.firstHoverHint")}
        </span>
      )}
    </span>
  );
};

export default ChatListRefV2;
